import React, {useMemo} from "react";
import {ExtendedCareEntrySearchResult} from "@components/CareEntryRow";
import {useExtendCareEntry} from "../../../../hooks/useCareEntryExtensions";
import {useReadableSlotTime} from "../../../../hooks/useReadableSlotTime";
import {SelectedLocation} from "@components/CareEntryRow/Control";
import {buildBookingLink, parseBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {useRouter} from "next/router";
import {hasRelatedCares} from "@components/Reason/CareDiscoverySearch/getAllRelatedCares";
import LeftIcon from "@components/CareEntryRow/LeftIcon";
import Line from "@components/CareEntryRow/Line";
import Tags from "@components/CareEntryRow/Tags";
import RightIcon from "@components/CareEntryRow/RightIcon";
import {useTranslation} from "@i18n/client";
import {I18nNamespace} from "@i18n";
import {RootStateApptReason} from "../../../../store/types";
import {usePreferredRegion} from "../../../../utils/regions/regionUtils";
import {useNextAvailableForReasons} from "@components/Booking/useNextAvailable";
import {useFetchNearbyLocationIdsQuery} from "@components/Booking/AppointmentReason/__generated__/fetch-nearby-location-ids.type";
import {useUserCoordinates} from "../../../../hooks/useGeolocateUser";
import {chooseSpecialty} from "@components/Locations/usePairLocsWithSlots";
import Link from "next/link";

const reasonMatching = (care: {slug: string}) => (reason: RootStateApptReason) =>
  reason.slug === care.slug;

/**
 * If this is an appointment reason, add it to reasonIdsSoFar and return it.
 * If it's a topic, concat reasonIdsSoFar with all the appointment reasons nested within it.
 */
const recursiveGetReasons = (
  searchResult: ExtendedCareEntrySearchResult,
  allReasons: RootStateApptReason[],
  reasonsSoFar: RootStateApptReason[] = [],
): RootStateApptReason[] => {
  const rootStateReason = allReasons.find(reasonMatching(searchResult));
  if (rootStateReason?.id) {
    return reasonsSoFar.concat(rootStateReason);
  }
  const childIds = searchResult.cares?.flatMap(care =>
    recursiveGetReasons(care, allReasons, reasonsSoFar),
  );
  return reasonsSoFar.concat(childIds ?? []);
};

/**
 * If care is an appointment reason, return it in an array.
 * If care is a topic, get all the appointment reasons nested within it.
 */
const getReasons = (
  care: ExtendedCareEntrySearchResult,
  allReasons: RootStateApptReason[],
): RootStateApptReason[] => recursiveGetReasons(care, allReasons, []);

type Props = {
  careEntry: ExtendedCareEntrySearchResult;
  query?: string;
  reasons: RootStateApptReason[];
  selectedLocation?: SelectedLocation;
};

export function Row({careEntry, query, reasons, selectedLocation}: Props) {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const extendedCareEntry = useExtendCareEntry(careEntry, reasons);
  const {slug} = extendedCareEntry;

  const isTopic = hasRelatedCares(extendedCareEntry);
  const thisReason = reasons.find(reasonMatching(extendedCareEntry));
  const specialtyId = chooseSpecialty(thisReason?.specialtyIds ?? []);

  const router = useRouter();
  const currentQuery = parseBookingQuery(router.query);

  const {data: userCoordinates} = useUserCoordinates({skip: !!selectedLocation});
  const {data: region} = usePreferredRegion(currentQuery.practiceId);
  const {data: nearbyLocations} = useFetchNearbyLocationIdsQuery({
    variables: {
      practiceId: currentQuery.practiceId,
      region: region.slug,
      latitude: userCoordinates?.x ?? 0,
      longitude: userCoordinates?.y ?? 0,
    },
    skip: !region || !!selectedLocation || !userCoordinates,
  });

  const nearbyLocationIds =
    nearbyLocations?.practice?.locationsNearest?.nodes?.map(location => location.id) ?? [];
  const relevantReasons = useMemo(
    () => getReasons(extendedCareEntry, reasons),
    [extendedCareEntry, reasons],
  );

  const href = isTopic
    ? buildBookingLink({
        ...currentQuery,
        selectedTopic: slug,
        showBackButton: true,
      })
    : buildBookingLink({
        ...currentQuery,
        appointmentReasonSlug: slug,
        region: region.slug,
        specialtyId,
        selectedTopic: null,
        showBackButton: null,
      });

  const {data: soonestSlot, isLoading} = useNextAvailableForReasons({
    locationIds: selectedLocation?.id ? [selectedLocation.id] : nearbyLocationIds,
    reasons: relevantReasons,
  });

  const readableSlotTime = useReadableSlotTime(soonestSlot?.time, {
    withNext: true,
  });

  const {icon = "defaultReasonIcon", name, price, turnaround} = extendedCareEntry;

  if (!isTopic && !specialtyId) {
    return;
  }

  return (
    <Link
      className="block w-full px-2 py-1 bg-white border-none rounded-lg sm:hover:bg-gray-100 hover:cursor-pointer"
      href={href}
    >
      <div className="flex justify-between items-center group">
        <LeftIcon icon={icon} size="lg" />
        <span className="flex flex-col justify-center items-start w-full">
          <Line keyPrefix="line1" className="mb-2" text={name} query={query} />
          <Tags
            turnaround={turnaround}
            price={price}
            isSlotLoading={isLoading}
            readableSlotTime={readableSlotTime || undefined}
          />
        </span>

        <div className="sm:group-hover:block hidden justify-center items-center text-center font-i cursor-pointer border border-solid box-border border-gray-800 bg-gray-1000 text-white text-sm leading-none font-semibold px-[18px] py-[13px] rounded-4xl">
          {i18n.t("Select")}
        </div>
        <div className="sm:group-hover:hidden block">
          <RightIcon />
        </div>
      </div>
    </Link>
  );
}
